// predict-chrome — the isolated "predict" surface (predict-activity [2.3], the keystone
// of PRIMM). Host chrome AROUND the unchanged runner: the teacher's program runs on the
// read-only client-runner with the READ-ONLY SOURCE CODE beside it (Ace, readOnly). The
// student types or picks a prediction and checks it with ONE button; the real code runs
// client-side and a reveal panel compares prediction vs actual output (✓ matched / ✗ not
// quite).
//
//   wire(ctx) — ctx = { core, payload, trinketId, clientsideMode, isOwner, editorApi, doc }
//
// Correctness is computed CLIENT-side (the expected output is SHIPPED in #predict-config,
// like the Code Challenge answer key) and the attempt is stored via `respond`. The shared
// #runner-run is HIDDEN (template CSS) and this chrome owns its OWN button. NO
// runner-contract change; editor-core untouched.
//
// ── activity-retry-and-close-on-success (Trello he7uDTaF) ────────────────────────────────
//
// A WRONG PREDICTION NO LONGER ENDS THE ACTIVITY. Being wrong is the point of a predict, and
// the shipped v1 froze the input, killed Run for good and showed the answer — a dead end that
// the classroom then ticked "Completed" while progressive reveal kept the next activity
// locked. So:
//
//   - On a MISS the real output is WITHHELD — the reveal's actual-output block is not shown,
//     the runner console is hidden from page load (template CSS, not JS: it prints stdout
//     live, so withholding only the panel withheld nothing), and on a graphics predict the
//     stage is COVERED. The student predicts again.
//   - After the class's existing attempt threshold a GIVE-UP appears. It reveals the output
//     and finishes the activity through the ORDINARY pass path — it stores `correct: true`
//     with `gaveUp: true` alongside — so the progressive-reveal wall clears exactly as a
//     genuine pass does. No new escape type, nothing a teacher checkbox can switch off.
//   - Reopening RESUMES: the attempt count is re-seeded from the server (without that, a
//     reload rewinds the give-up gate AND the next POST overwrites the recorded count
//     downwards), a mid-retry predict is pre-filled and still answerable, and a finished one
//     shows its stored outcome and may still be RUN but not re-submitted.
//
// FOUR PIECES OF SURFACE STATE MUST BE RESET PER ATTEMPT, and each one silently disables part
// of the feature if it is not: `revealed`, `submitted` (left latched, attempts 2..n never
// POST at all — this card's exact symptom, rebuilt by its own fix), the run accumulators, and
// the run gate. None of it is visible to any automated gate; it is a browser check.
//
// ── activity-action-buttons (Trello uLvKu9QZ) ────────────────────────────────────────────
//
// THE COMMIT STEP IS GONE. "Lock in prediction" did almost nothing — it did not freeze the
// input and did not snapshot the answer; it only unlocked Run and, after a miss, forced a
// re-commit. There is now ONE button ("Check prediction", relabelled "Run it again" once the
// activity is finished), enabled whenever the runner is READY, nothing is RUNNING or SAVING,
// and either a prediction exists or the activity is finished. Three rules hold it together:
//
//   - NO CHANGE-GATE. An unchanged prediction may be re-checked and it DOES count as an
//     attempt. Requiring a change would make the give-up (gated on `attempts >= threshold`)
//     unreachable for a student who will not change their answer, breaking the shipped
//     guarantee that a student can always eventually see the answer and finish. Consequence,
//     accepted: `attemptThreshold` now effectively means "graded runs".
//   - THE GIVE-UP IS NEVER GATED ON `running` OR `saving`. `running` is cleared only by a
//     terminal state event or the graphics settle timer — `core.on('error')` does not clear
//     it and #runner-stop is hidden by template CSS — so a NON-TERMINATING program leaves it
//     true for the life of the page. Gating the give-up on it would leave a student with no
//     run, no stop and no way to the answer. `saving` has the same shape on a stalled fetch.
//   - THE PREDICTION IS SNAPSHOTTED AT RUN START, in both modes, and that snapshot is what is
//     graded, revealed and stored — see `runText` / `runIdx` below.

// Read + parse the server-embedded compare config (already '<'-escaped by the handler).
function readConfig(doc) {
  var el = doc.getElementById('predict-config');
  if (!el) { return null; }
  try {
    var cfg = JSON.parse(el.textContent || '{}');
    if (!cfg) { return null; }
    // Teacher capture surface: no student `mode`, just { capture:true, … }.
    if (cfg.capture === true) { return cfg; }
    if (cfg.mode !== 'text' && cfg.mode !== 'choice') { return null; }
    return cfg;
  } catch (e) { return null; }
}

// ── predict-answer-feedback (Trello wsnTevds) ────────────────────────────────────────────
//
// A text predict gains three optional, per-activity leniency toggles (ignore capital
// letters / punctuation / extra spaces, all default OFF), a wrong answer says what KIND of
// thing was wrong without revealing the real output, and a pass that leniency carried says
// so and offers the way back instead of counting down to close.
//
// The comparison, the diagnostic and the pass note are all PURE and live in
// `predict-diagnostic.js` so they can be unit-tested on the host — grading is entirely
// client-side here, so nothing else can check them. This file owns only the DOM half.
//
// The local `normalizeOutput` that used to sit here is GONE: it was a third normalizer
// beside the shared `challenge-grading.js` one. `normalizePredictOutput` is a thin wrapper
// over that shared helper which re-applies predict's leading-blank-line strip, so today's
// single-toggle output is reproduced byte-for-byte.
import {
  readSettings,
  gradePrediction,
  diagnosePredictMiss,
  explainForgivenPass,
  comparePrediction
} from './predict-diagnostic.js';

// The route's Joi bound on the stored strings — the captured output AND (activity-action-
// buttons) the stored PREDICTION. TRUNCATE ON THE CLIENT: program output is machine-generated
// and routinely long, a pasted prediction can be too, the respond route has no top-level
// fail:{code:400}, and an over-long value therefore yields a benign 200 with the handler
// SKIPPED and NOTHING stored — while the client writes "Saved to your progress." because
// `res.ok` is true. That silently reintroduces the very lock-out this change removes.
// GRADING always uses the untruncated value; only STORAGE is capped.
var MAX_STORED_OUTPUT = 5000;

import { mountPreviewStrip } from './preview-strip.js';
import { offerClose, offerReturn } from './close-prompt.js';

export function wire(ctx) {
  var doc  = ctx.doc;
  var core = ctx.core;
  var cfg  = readConfig(doc);
  if (!cfg) { return; }

  // teacher-preview-split (tasks 9.1/9.3): SERVER-RESOLVED preview mode. `cfg.preview` was
  // set only after the route's pre-handler confirmed `isCourseStaff` (R17), so a student who
  // opens a shared or bookmarked preview-flagged URL sees no strip and keeps persisting
  // exactly as today.
  var previewMode = cfg.preview === true;
  // one-tab-same-tab-launches (Trello bD1oe35m): the activity top bar's preview chip and "Back to
  // class" replace the strip, so it is mounted only when the bar is absent (card 2's guard).
  if (!doc.getElementById('surface-topbar-back')) {
    mountPreviewStrip(doc, { active: previewMode, exit: 'reload' });
  }

  var panel = doc.getElementById('predict-panel');
  if (!panel) { return; }

  var mode        = cfg.mode;
  var isChoice    = mode === 'choice';
  var choices     = Array.isArray(cfg.choices) ? cfg.choices : [];
  // predict-answer-feedback: the activity's own leniency settings, every key resolved.
  // `trailingWhitespace` is absent-means-ON; the three optional relaxations are
  // absent-means-OFF, so an activity carrying none of them grades exactly as it did before
  // this change. `predictRun` ships `predict.normalize` whole, so no new key is needed on
  // the payload side.
  var normalizeCfg = readSettings(cfg.normalize);
  var canManage   = !!cfg.canManage;

  // activity-retry-and-close-on-success: the two new server-resolved facts.
  //
  // `attemptThreshold` is the class's EXISTING progressive-reveal "offer skip and reveal
  // after this many failed attempts" number, resolved server-side (floored at 1, defaulting
  // to 2 where reveal is off) and shipped as a bare NUMBER — never the settings object, which
  // carries a teacher-only notification preference. It gates WHEN the give-up appears, never
  // WHETHER: it is deliberately not tied to the per-class escape checkboxes, because in the
  // class that reported this card both of those are switched off.
  var threshold = (typeof cfg.attemptThreshold === 'number' && isFinite(cfg.attemptThreshold))
    ? Math.max(1, Math.floor(cfg.attemptThreshold)) : 2;
  // `resumed` is this student's own stored record for THIS class (server-side, class-scoped,
  // never in preview mode), already given its legacy reading: absent attempts read as 1,
  // absent gaveUp as false, and an absent `actual` stays ABSENT rather than becoming '' —
  // which `setOutput` would print as "(no output)", a false claim about the program.
  var resumed   = (cfg.resumed && typeof cfg.resumed === 'object') ? cfg.resumed : null;
  var resumedActual = (resumed && typeof resumed.actual === 'string') ? resumed.actual : null;

  // The runner language mode ('' pgzero, 'pygame', 'python'). A GRAPHICS program draws to
  // the canvas and — for pgzero/pygame — never terminates: its event loop runs until the
  // student leaves. Waiting for a terminal state (as a python predict does) would hang the
  // reveal forever, so a graphics predict reveals once the program is actually RUNNING and
  // leaves the loop going, so the student can watch what they predicted.
  var runMode    = (ctx.payload && ctx.payload.mode) || ctx.clientsideMode || '';
  var isGraphics = runMode !== 'python';

  // ms to let the first frame paint before the reveal slides in. The runner reports the
  // pgzero loop start only as a diagnostic (no event — the runner contract is frozen), so
  // this is a settle, not a handshake. Correctness never depends on it: choice mode grades
  // on the answer index, and the canvas keeps running either way.
  var GRAPHICS_SETTLE_MS = 1200;

  // The shared "Press Run to start your program" stage overlay (clientside-base.html) is
  // opaque and hides itself on a #runner-run click — a button this surface deliberately
  // hides. So predict-chrome must retire it on its OWN Run, or the canvas stays covered
  // and a perfectly good run looks like nothing happened.
  function hidePrerunOverlay() {
    var prerun = doc.getElementById('stage-prerun');
    if (prerun) { prerun.hidden = true; }
  }

  // ── surface state ─────────────────────────────────────────────────────────
  var ready       = false;   // runner ready (run() is a no-op before this)
  var running     = false;   // a run is in flight (reveal fires on its terminal state)
  // activity-action-buttons: a response POST is in flight. It guards the RUN BUTTON ONLY —
  // NEVER the give-up (see the header). Its purpose is the overlapping-POST race: the
  // miss-path POST is unawaited and `submitted` resets per run, so on a graphics predict the
  // settle timer below can fire POST n+1 while n is still in flight, and `persistResponse` is
  // read-then-write, so an out-of-order landing stores the LOWER attempt count. The one-click
  // loop is what makes that newly reachable.
  var saving      = false;
  var revealed    = false;   // the reveal has been shown FOR THIS GO (reset per attempt)
  var submitted   = false;   // the response has been POSTed FOR THIS GO (reset per attempt)
  // Bumped per go. A graphics settle timer from an EARLIER go must not fire against a LATER
  // one: `running` and `revealed` are both reset by the next run, so they cannot tell the two
  // apart on their own.
  var runToken    = 0;
  var selectedIdx = -1;      // choice mode: the chosen option index
  var finished    = false;   // matched, or gave up — the activity is over
  var gaveUp      = false;   // the student asked to be shown the answer

  // `attempts` counts SUBMITTED PREDICTIONS ONLY — a give-up does not increment it. It is
  // RE-SEEDED from the stored value, without which a reload rewinds the give-up gate and the
  // next POST writes the count back down, making the recorded figure untrue.
  var attempts       = resumed ? resumed.attempts : 0;
  var lastPrediction = resumed ? resumed.prediction : undefined;

  // activity-action-buttons: THE PREDICTION AS IT STOOD WHEN THE RUN STARTED. Grading, the
  // reveal, `lastPrediction` and the stored payload all read these — never the live control —
  // so the verdict, the reveal and the stored record can never disagree about what was
  // predicted. Two distinct hazards, both opened by the one-click loop:
  //   - TEXT (storage): the `input` listener cannot cancel an in-flight run, so a student who
  //     clears the box mid-run would otherwise send `prediction: ''`, which fails BOTH Joi
  //     alternatives → benign 200 → NOTHING stored, while the UI writes "Saved to your
  //     progress." because `res.ok` is true.
  //   - CHOICE (grading integrity): the radio `change` listener is guarded only by
  //     `if (finished)`, not by `running`, so a student could start a run, switch to the
  //     correct option mid-run (or during the 1.2 s graphics settle) and be graded correct on
  //     a run made with a different answer. `selectedIdx` never returns to -1, so choice mode
  //     has no empty-payload hazard — this half is correctness, not storage.
  var runText = '';          // text mode: textarea.value at run start
  var runIdx  = -1;          // choice mode: selectedIdx at run start
  // What the student SAW as the actual output on their most recent run this session. This is
  // what gets stored — never the raw stdout accumulator, whose notes and stdin guidance are
  // part of what the student was shown.
  var sessionActual  = null;

  var stdoutAcc   = '';      // accumulated STDOUT for this run
  var errorText   = '';      // stderr / error detail for this run
  var errored     = false;   // a runner/student error occurred this run
  var inputAsked  = false;   // the program requested stdin (non-interactive guidance)

  // ── DOM ───────────────────────────────────────────────────────────────────
  function el(tag, cls, text) {
    var n = doc.createElement(tag);
    if (cls) { n.className = cls; }
    if (text != null) { n.textContent = text; }
    return n;
  }

  // ── teacher capture mode ─────────────────────────────────────────────────────
  // Opened from the authoring dialog ("Fill in the correct output"): run the
  // reference program ONCE and hand its exact stdout back to the dialog via a
  // same-origin BroadcastChannel (the isolated page can't reach window.opener
  // under COOP). No prediction gate, no respond POST — this is not a student attempt.
  //
  // NOTE FOR THE WITHHOLDING CSS: this branch renders the SAME template, so it carries
  // `body[data-surface="predict"]` too — and it returns here, before any of the code that
  // restores the console or unsuppresses the status line. Every rule added for the student
  // surface therefore keys off `body[data-predict-student]`, which only the student route
  // emits; a blanket surface rule would leave a teacher capturing a program that raises with
  // no traceback and no console anywhere.
  if (cfg.capture) {
    var capAccum = '';
    var capRunning = false;
    var capDone = false;

    panel.appendChild(el('h2', 'predict-heading', 'Capture the correct output'));
    var capSub = el('p', 'predict-sub', 'Running the program to capture exactly what it prints…');
    panel.appendChild(capSub);
    var capOut = el('pre', 'predict-output');
    capOut.hidden = true;
    panel.appendChild(capOut);
    var capActions = el('div', 'predict-actions');
    var capUse = el('button', 'predict-btn predict-btn-run', 'Use this as the correct answer');
    capUse.type = 'button';
    capUse.disabled = true;
    var capCancel = el('button', 'predict-btn predict-btn-lock', 'Cancel');
    capCancel.type = 'button';
    capActions.appendChild(capUse);
    capActions.appendChild(capCancel);
    panel.appendChild(capActions);

    var capFinish = function () {
      if (capDone) { return; }
      capDone = true; capRunning = false;
      capSub.textContent = 'This is exactly what the program printed. Use it as the correct answer, or cancel.';
      capOut.textContent = capAccum === '' ? '(no output)' : capAccum;
      capOut.hidden = false;
      capUse.disabled = false;
    };
    core.on('ready', function () { capRunning = true; hidePrerunOverlay(); core.run(); });
    core.on('stdout', function (t) { if (capRunning) { capAccum += String(t == null ? '' : t); } });
    core.on('input-request', function () { if (capRunning) { core.stop(); } });
    core.on('state', function (state) {
      if (!capRunning) { return; }
      if (state === 'finished' || state === 'stopped'
          || state === 'student-error' || state === 'runner-error') { capFinish(); }
    });
    capUse.addEventListener('click', function () {
      try {
        var bc = new BroadcastChannel('pc-predict-capture');
        bc.postMessage({ output: capAccum });
        bc.close();
      } catch (e) { /* no BroadcastChannel — teacher can copy the output manually */ }
      try { window.close(); } catch (e) { /* ignore */ }
    });
    capCancel.addEventListener('click', function () { try { window.close(); } catch (e) { /* ignore */ } });
    return;
  }

  var heading = el('h2', 'predict-heading', 'Make your prediction');
  var sub = el('p', 'predict-sub', isChoice
    ? 'Read the code on the left. Which output do you think it produces?'
    : 'Read the code on the left. Work out what it prints and type it — before you run it.');
  panel.appendChild(heading);
  panel.appendChild(sub);

  // ONE always-present live region, separate from the reveal panel below.
  //
  // The reveal is `hidden`-toggled and its content is written before it is unhidden, which
  // does not reliably announce — so a non-sighted student would learn nothing about being
  // wrong, and under the retry design "wrong" is the moment that matters most. This element
  // exists from page load and is only ever written to.
  var announcer = el('p', 'predict-sr');
  announcer.setAttribute('role', 'status');
  panel.appendChild(announcer);
  function announce(message) {
    announcer.textContent = '';
    announcer.textContent = message;
  }

  // Prediction input (textarea for text mode; a radio list for choice mode).
  var textarea = null;
  var radios = [];
  if (isChoice) {
    var list = el('div', 'predict-choices');
    list.setAttribute('role', 'radiogroup');
    list.setAttribute('aria-label', 'Predicted output');
    // Randomize the DISPLAY order per load so the correct answer isn't always in
    // the same position. `i` stays the ORIGINAL index, so selection + grading
    // (selectedIdx === answerIndex) and the stored prediction are unaffected.
    var order = choices.map(function (_o, k) { return k; });
    for (var s = order.length - 1; s > 0; s--) {
      var rnd = Math.floor(Math.random() * (s + 1));
      var swap = order[s]; order[s] = order[rnd]; order[rnd] = swap;
    }
    order.forEach(function (i) {
      var opt = choices[i];
      var label = el('label', 'predict-choice');
      var input = doc.createElement('input');
      input.type = 'radio';
      input.name = 'predict-choice';
      input.value = String(i);
      var span = el('span', 'predict-choice-text', String(opt == null ? '' : opt));
      label.appendChild(input);
      label.appendChild(span);
      input.addEventListener('change', function () {
        if (finished) { return; }
        selectedIdx = i;
        radios.forEach(function (r) { r.label.classList.remove('is-selected'); });
        label.classList.add('is-selected');
        // activity-action-buttons: THIS LISTENER IS LOAD-BEARING. With the commit step gone it
        // is the only thing that enables the button as the student answers (the predicate
        // reads `hasPrediction()`), and the only thing that moves the hint chain off "Pick
        // your prediction first." and between its changed / unchanged states.
        updateRunEnabled();
      });
      list.appendChild(label);
      radios.push({ input: input, label: label, index: i });
    });
    panel.appendChild(list);
  }
  else {
    textarea = el('textarea', 'predict-textarea');
    textarea.setAttribute('aria-label', 'Predicted output');
    textarea.setAttribute('placeholder', 'What will this program print?');
    // graded-input-hardening (Trello Vov6hPyT): defensive browser/extension text-prediction
    // opt-out — could not reproduce or confirm the reported corruption in-app, this is
    // best-effort, not a proven fix.
    textarea.setAttribute('autocomplete', 'off');
    textarea.setAttribute('spellcheck', 'false');
    textarea.setAttribute('autocapitalize', 'off');
    textarea.setAttribute('autocorrect', 'off');
    textarea.setAttribute('data-gramm', 'false');
    textarea.setAttribute('data-gramm_editor', 'false');
    textarea.setAttribute('data-enable-grammarly', 'false');
    // activity-action-buttons: the cap is enforced where the student TYPES as well as where
    // the payload is built, so the grade/store divergence is practically unreachable rather
    // than merely handled. Grading still uses the untruncated value.
    textarea.setAttribute('maxlength', String(MAX_STORED_OUTPUT));
    textarea.addEventListener('input', function () {
      if (finished) { return; }
      // LOAD-BEARING — see the radio listener above.
      updateRunEnabled();
    });
    panel.appendChild(textarea);
  }

  // Actions — activity-action-buttons: ONE control. The hint below is the button's accessible
  // DESCRIPTION, so the chain in `updateRunEnabled()` is the only thing that ever writes it
  // and is guaranteed never to be empty. No text is set here: construction calls
  // `updateRunEnabled()` at the end of `wire()`, which fills both the label and the hint.
  var actions = el('div', 'predict-actions');
  var runBtn = el('button', 'predict-btn predict-btn-run', 'Check prediction');
  runBtn.type = 'button';
  runBtn.disabled = true;
  var hint = el('p', 'predict-hint');
  hint.setAttribute('id', 'predict-hint');
  runBtn.setAttribute('aria-describedby', 'predict-hint');
  actions.appendChild(runBtn);
  actions.appendChild(hint);
  panel.appendChild(actions);

  if (canManage) {
    panel.appendChild(el('div', 'predict-preview-note',
      'Preview mode. Nothing you do here is saved.'));
  }

  // Reveal (hidden until the run finishes).
  var reveal = el('div', 'predict-reveal');
  reveal.hidden = true;
  var verdict = el('div', 'predict-verdict');
  var yourLabel = el('p', 'predict-block-label', 'Your prediction');
  var yourOut = el('pre', 'predict-output');
  var actualLabel = el('p', 'predict-block-label', 'Actual output');
  var actualOut = el('pre', 'predict-output');
  var reflect = el('p', 'predict-reflect', '');
  // predict-answer-feedback: the forgiven-pass note. A NEW element with its OWN class,
  // never `.predict-hint` — that one is the Run button's `aria-describedby` and is fenced
  // off. It is BUILT ONLY IN TEXT MODE, so choice mode's reveal panel is byte-unchanged
  // (this card is text-only, and choice is index equality with nothing to relax); the
  // teacher CAPTURE surface never reaches here at all, having returned far above.
  //
  // There is deliberately NO explicit clear: a pass is terminal (freeze-on-pass, `showReveal`
  // early-returns on a re-run of a finished predict, and the give-up handler's own
  // `if (finished || …) return;` makes a post-pass give-up unreachable), so nothing can
  // follow it that would need the element emptied.
  var forgivenNote = null;
  var saveStatus = el('p', 'predict-save-status', '');
  reveal.appendChild(verdict);
  reveal.appendChild(yourLabel);
  reveal.appendChild(yourOut);
  reveal.appendChild(actualLabel);
  reveal.appendChild(actualOut);
  reveal.appendChild(reflect);
  if (!isChoice) {
    forgivenNote = el('p', 'predict-forgiven', '');
    forgivenNote.hidden = true;
    reveal.appendChild(forgivenNote);
  }
  reveal.appendChild(saveStatus);
  panel.appendChild(reveal);

  // The give-up. Present from page load in the DOM (so its appearance is a single hidden
  // toggle rather than a layout insertion) and disabled until an answer is genuinely
  // available to show.
  var giveUpWrap = el('div', 'predict-giveup');
  giveUpWrap.hidden = true;
  var giveUpBtn = el('button', 'predict-btn predict-btn-giveup', 'Show me the answer');
  giveUpBtn.type = 'button';
  var giveUpNote = el('p', 'predict-hint', 'You can see the answer and move on — this is recorded as shown, not worked out.');
  giveUpWrap.appendChild(giveUpBtn);
  giveUpWrap.appendChild(giveUpNote);
  panel.appendChild(giveUpWrap);

  // ── the stage cover ───────────────────────────────────────────────────────
  //
  // A graphics predict draws its answer on the canvas, so on a miss the picture has to go —
  // but there is NO runner API to blank it: `stop()` never clears the canvas and `restart()`
  // reloads Pyodide. So the program is COVERED, never stopped, and keeps running underneath.
  //
  // A NEW element, never `#stage-prerun`: that overlay derives its availability from the
  // hidden #runner-run, so re-showing it yields a DISABLED button captioned "Press Run to
  // start your program", and it re-hides itself on the next `running` state.
  //
  // `aria-hidden` decoration over an `inert` frame: without the inert the cover is a picture
  // over a live program a keyboard student can still tab into and play.
  var stageEl = doc.querySelector('.runner-stage');
  var runnerFrame = doc.getElementById('runner-frame');
  var cover = null;
  if (stageEl && isGraphics) {
    cover = el('div', 'predict-cover');
    cover.setAttribute('aria-hidden', 'true');
    cover.hidden = true;
    cover.appendChild(el('p', 'predict-cover-text', 'Hidden while you predict again'));
    stageEl.appendChild(cover);
  }
  var coverOn = false;
  function setCover(on) {
    if (!cover) { return; }
    coverOn = !!on;
    cover.hidden = !on;
    updatePromptVisibility();
    if (!runnerFrame) { return; }
    try {
      if (on) { runnerFrame.setAttribute('inert', ''); runnerFrame.inert = true; }
      else { runnerFrame.removeAttribute('inert'); runnerFrame.inert = false; }
    } catch (e) { /* `inert` is progressive enhancement over the visual cover */ }
  }

  // ── predict-run-prompt (Trello JQAWfRb5): no student predict says "press Run" ──────────
  //
  // #runner-run is hidden here, so the shared stage overlay ("Press Run to start your
  // program", a dead disabled button) and editor-core's "Ready — press Run." status both
  // point at a control that does not exist. Graphics predicts get their OWN prompt (a real
  // button that moves the student to the prediction panel — it never runs anything); every
  // predict gets the status line rewritten. Exact-match only, so no other status is clobbered.
  var READY_RUN = 'Ready — press Run.';
  var CAPTION_PICK = 'Pick your prediction, then press Check prediction to run it.';
  var CAPTION_TYPE = 'Type your prediction, then press Check prediction to run it.';
  var CAPTION_DONE = 'Press Run it again to run the program.';
  var STATUS_PICK = 'Ready — pick your prediction, then press Check prediction.';
  var STATUS_TYPE = 'Ready — type your prediction, then press Check prediction.';
  var STATUS_DONE = 'Ready — press Run it again.';

  function syncReadyStatus() {
    var st = doc.getElementById('runner-status');
    if (!st) { return; }
    var t = st.textContent;
    if (t === READY_RUN) {
      st.textContent = finished ? STATUS_DONE : (isChoice ? STATUS_PICK : STATUS_TYPE);
    } else if (finished && (t === STATUS_PICK || t === STATUS_TYPE)) {
      st.textContent = STATUS_DONE;
    }
  }

  var hasRunThisPage = false;
  var stagePrompt = null;
  var promptBtn = null;
  var promptText = null;
  if (stageEl && isGraphics) {
    stagePrompt = el('div', 'predict-stage-prompt');
    promptBtn = el('button', 'predict-stage-prompt-btn');
    promptBtn.type = 'button';
    var ring = el('span', 'predict-stage-prompt-ring');
    ring.setAttribute('aria-hidden', 'true');
    ring.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
    promptText = el('span', 'predict-stage-prompt-text', isChoice ? CAPTION_PICK : CAPTION_TYPE);
    promptBtn.appendChild(ring);
    promptBtn.appendChild(promptText);
    stagePrompt.appendChild(promptBtn);
    var prerunEl = doc.getElementById('stage-prerun');
    if (prerunEl && prerunEl.parentNode === stageEl && prerunEl.nextSibling) {
      stageEl.insertBefore(stagePrompt, prerunEl.nextSibling);
    } else { stageEl.appendChild(stagePrompt); }
    try { doc.body.classList.add('predict-stage-prompt-on'); } catch (e) { /* ignore */ }
  }
  function updatePromptVisibility() {
    if (!stagePrompt) { return; }
    stagePrompt.hidden = hasRunThisPage || coverOn;
  }
  function updatePromptCaption() {
    if (!promptText) { return; }
    var c = finished ? CAPTION_DONE : (isChoice ? CAPTION_PICK : CAPTION_TYPE);
    if (promptText.textContent !== c) { promptText.textContent = c; }
  }
  var highlightTimer = null;
  var highlightedEl = null;
  function highlight(node) {
    if (!node || !node.classList) { return; }
    if (highlightTimer !== null) { clearTimeout(highlightTimer); highlightTimer = null; }
    if (highlightedEl && highlightedEl.classList) { highlightedEl.classList.remove('predict-stage-target'); }
    node.classList.add('predict-stage-target');
    highlightedEl = node;
    highlightTimer = setTimeout(function () {
      node.classList.remove('predict-stage-target');
      highlightTimer = null;
      highlightedEl = null;
    }, 1200);
  }
  // The prediction input: checked radio, else first radio; or the textarea.
  function inputTarget() {
    if (isChoice) {
      for (var i = 0; i < radios.length; i++) { if (radios[i].input.checked) { return radios[i]; } }
      return radios[0] || null;
    }
    return textarea ? { input: textarea, label: textarea } : null;
  }
  if (promptBtn) {
    promptBtn.addEventListener('click', function () {
      var focusEl = null;
      var hlEl = null;
      if (!finished && !hasPrediction()) {
        var first = isChoice ? (radios[0] || null) : inputTarget();
        if (first) { focusEl = first.input; hlEl = first.label; }
      } else {
        focusEl = runBtn; hlEl = runBtn;
      }
      if (hlEl && typeof hlEl.scrollIntoView === 'function') {
        try { hlEl.scrollIntoView({ block: 'nearest' }); } catch (e) { /* ignore */ }
      }
      highlight(hlEl);
      if (focusEl && focusEl.disabled) {
        focusEl = null;
        if (!finished) {
          var alt = inputTarget();
          if (alt && !alt.input.disabled) { focusEl = alt.input; }
        }
      }
      if (focusEl && typeof focusEl.focus === 'function') { focusEl.focus(); }
    });
  }
  core.on('state', function (state) {
    if (state === 'booting' || state === 'running') {
      hasRunThisPage = true;
      updatePromptVisibility();
    }
  });

  // ── withholding / revealing the real output ───────────────────────────────
  //
  // The console is hidden by TEMPLATE CSS from page load — genuinely `display:none`, not
  // clipped, so its `aria-live` region cannot read the answer out to a screen-reader student
  // while the sighted student sees nothing. Restoring it is one body class, and it happens
  // ONLY when the answer is EARNED (a match) or REQUESTED (a give-up) — never merely because
  // the reveal panel appeared on a miss.
  function showAnswerSurfaces() {
    try { doc.body.classList.add('predict-answer-shown'); } catch (e) { /* ignore */ }
    setCover(false);
  }

  // ── gating ────────────────────────────────────────────────────────────────
  function hasPrediction() {
    if (isChoice) { return selectedIdx >= 0; }
    return textarea && textarea.value.trim() !== '';
  }

  // activity-action-buttons: case 6 of the hint chain compares the LIVE prediction against the
  // last GRADED one. Two things about this are load-bearing:
  //   - the baseline is `lastPrediction` (initialised at its declaration from
  //     `resumed.prediction`, so it is defined at EVERY evaluation including construction),
  //     never a fresh per-run snapshot. A per-run snapshot is still `undefined` on a RESUMED
  //     MID-RETRY load — the commonest path of all — so the comparison would come out unequal
  //     and fire "Check your new prediction." on a load where the student changed nothing,
  //     contradicting the "✗ Not quite yet" verdict `restore()` has just painted.
  //   - the comparison is PER MODE: choice compares Numbers, text compares Strings. A bare
  //     `!==` across a legacy record whose stored `prediction` is a Number while the textarea
  //     holds "2" fires case 6 spuriously.
  function predictionDiffersFromGraded() {
    if (lastPrediction === undefined) { return false; }
    if (isChoice) { return Number(selectedIdx) !== Number(lastPrediction); }
    return String(textarea ? textarea.value : '') !== String(lastPrediction);
  }

  // THE SINGLE POINT where the button's ENABLED state, its LABEL and its HINT are all decided,
  // so they cannot drift apart. Every re-evaluation point must call it: typing / radio change,
  // `ready`, run start, run settle (all four terminal states AND the graphics settle timer),
  // SAVE START, SAVE SETTLE, the give-up, and once at construction. Without the save pair the
  // `saving` conjunct is inert — on the miss path this runs BEFORE `submitResponse()`, so the
  // button would stay visibly enabled through the whole POST while the click handler silently
  // swallowed the click, which is exactly the enabled-and-inert state the predicate forbids.
  function updateRunEnabled() {
    // A FINISHED predict may still be run: re-watching the program is the whole point of the
    // pgzero predicts, and the read-only rule governs the ANSWER, not the runner. It just no
    // longer submits anything (`showReveal`'s `finished` early-exit guarantees that) and it
    // must not re-offer to close the tab.
    //
    // THIS PREDICATE IS DUPLICATED VERBATIM IN THE CLICK HANDLER BELOW. Keep the two textually
    // identical: a click handler stricter than this leaves the button enabled and inert.
    runBtn.disabled = !(ready && !running && !saving && (hasPrediction() || finished));

    // The relabel lives HERE, with the enabled state, so the two can never disagree. It is
    // deliberately NOT announced: a silent accessible-name change competing with the
    // close-prompt's aria-modal scrim and the role="status" message is worse than nothing.
    // Written only when it actually changes — this function runs on every keystroke, and
    // re-writing `textContent` replaces the button's accessible name node each time.
    var label = finished ? 'Run it again' : 'Check prediction';
    if (runBtn.textContent !== label) { runBtn.textContent = label; }
    updatePromptCaption();
    syncReadyStatus();

    // ── the hint chain ────────────────────────────────────────────────────────
    //
    // TOTAL, ORDERED, FIRST MATCH WINS, NEVER EMPTY. It is the button's `aria-describedby`,
    // so an empty value would leave a control with no description at all — and EVERY
    // disabling condition outranks every case that would promise an action, so the
    // description can never invite a student to press a disabled control.
    //
    // Readiness (3) OUTRANKS `finished` (4) on purpose: a resumed-finished predict boots
    // Pyodide with the button disabled, and case 4 would otherwise promise "you can still run
    // the program" as the description of a disabled control. Same for the give-up-before-ready
    // path. `saving` (2) is present for the same reason.
    var text;
    if (running) {
      text = 'Running…';
    }
    else if (saving) {
      text = 'Saving…';
    }
    else if (!ready) {
      text = 'Loading runner…';
    }
    else if (finished) {
      text = 'You have finished this one. You can still run the program to watch it again.';
    }
    else if (!hasPrediction()) {
      text = isChoice ? 'Pick your prediction first.' : 'Type your prediction first.';
    }
    // Case 6 exists because case 7 asserts a VERDICT. Without it, the moment a student does
    // the one thing the hint told them to do — edit their prediction — the description would
    // still report a verdict on the OLD answer plus an instruction they had already carried
    // out. Case 8 is unreachable after the first miss, so this is the only "you've changed it,
    // press check" state. The predicate is `attempts >= 1 && !finished`, not a session flag:
    // the two diverge on a resumed mid-retry load, where only the `attempts` form matches the
    // "✗ Not quite yet" already on screen.
    else if (attempts >= 1 && !finished && predictionDiffersFromGraded()) {
      text = 'Check your new prediction.';
    }
    // Case 7 carries the ATTEMPT NUMBER because a re-check must be perceptible on EVERY press.
    // From miss 3 onward the verdict, prediction block, reflect line, cover and withheld
    // actual block all re-render byte-identically, and a class with a threshold above 2 would
    // otherwise spend every press between miss 2 and miss N with no feedback at all. The
    // counter belongs here rather than on case 6, which by construction never fires on a
    // re-press of an UNCHANGED prediction — precisely the state this has to make perceptible.
    else if (attempts >= 1 && !finished) {
      text = 'Not a match yet (attempt ' + attempts + '). Look at the code again, then change '
           + 'your prediction and check it.';
    }
    else {
      text = 'Run the program to check your prediction.';
    }
    hint.textContent = text;
  }

  // The give-up is gated on WHEN, never WHETHER — after `threshold` failed goes — and is
  // disabled until an answer is genuinely available to show.
  //
  // "Available" is a CLIENT-SIDE condition, never the persisted `actual` key: that key is
  // deliberately never stored for a choice-mode predict (its reveal derives from the answer
  // index) nor for a `mode:'text'` graphics predict (its stdout is the SDL boot banner), so a
  // returning student on either sub-type would find the give-up permanently disabled.
  //
  // It NEVER falls back to `cfg.expectedOutput`. That is the teacher-typed value this
  // surface deliberately distrusts — grading has always been against what the program really
  // printed, because a teacher's typed answer can simply be wrong.
  function answerAvailable() {
    if (isChoice) { return cfg.answerIndex >= 0 && choices[cfg.answerIndex] != null; }
    if (typeof sessionActual === 'string') { return true; }
    return typeof resumedActual === 'string';
  }
  // activity-action-buttons: THIS FUNCTION IS DELIBERATELY UNCHANGED, and must stay that way.
  // The give-up may NEVER be gated on `running` or `saving` — see the header for why a
  // non-terminating program or a stalled fetch would otherwise strand a student with no way
  // to the answer at all.
  function updateGiveUp() {
    var offer = !finished && attempts >= threshold;
    giveUpWrap.hidden = !offer;
    if (!offer) { return; }
    var can = answerAvailable();
    giveUpBtn.disabled = !can;
    giveUpNote.textContent = can
      ? 'You can see the answer and move on — this is recorded as shown, not worked out.'
      : 'Check your prediction once more and you can ask to see the answer.';
  }

  runBtn.addEventListener('click', function () {
    // THE SAME PREDICATE AS `updateRunEnabled()` ABOVE, VERBATIM. If the two ever diverge the
    // button is enabled-and-inert (or worse, inert-and-enabled the other way round).
    if (!(ready && !running && !saving && (hasPrediction() || finished))) { return; }
    running = true;
    // activity-action-buttons: the prediction is FROZEN here, for the whole of this go. The
    // live control stays editable — the retry design needs that — but nothing downstream reads
    // it again. See `runText` / `runIdx`.
    runText = textarea ? textarea.value : '';
    runIdx  = selectedIdx;
    // EVERY piece of per-go state resets here. `submitted` in particular: left latched from
    // the first POST, attempts 2..n would never store anything at all — the count would stick
    // at 1, the give-up gate would never re-seed on reload, and a student who eventually
    // MATCHED would store nothing, so the wall would never clear.
    revealed = false;
    submitted = false;
    runToken += 1;
    stdoutAcc = ''; errorText = ''; errored = false; inputAsked = false;
    updateRunEnabled();
    hidePrerunOverlay();
    core.run();
  });

  // ── run listeners ───────────────────────────────────────────────────────────
  core.on('ready', function () {
    ready = true;
    updateRunEnabled();
  });
  core.on('stdout', function (text) { if (running) { stdoutAcc += String(text == null ? '' : text); } });
  core.on('stderr', function (text) { if (running) { errorText += String(text == null ? '' : text); } });
  core.on('error', function (detail) {
    if (!running) { return; }
    errored = true;
    var d = detail && detail.detail ? detail.detail : detail;
    if (d != null) { errorText += String(d); }
    // The status line stays VISIBLE (it carries "Loading runner…" and genuine boot failures)
    // but its program-error text is replaced HERE, at error time — not at verdict time.
    // editor-core writes the traceback into that `aria-live` element the instant it arrives,
    // long before anyone knows whether the prediction matched, and a traceback can BE the
    // answer. A boot failure happens outside a run and so never reaches this branch.
    if (!finished) {
      var statusEl = doc.getElementById('runner-status');
      if (statusEl) {
        statusEl.textContent = 'The program stopped early.';
        statusEl.classList.remove('runner-error');
      }
    }
  });
  // Predict targets NON-interactive programs. If the runner parks on input(), we cannot
  // feed EOF (editor-core exposes no programmatic stdin send), so stop() the parked run —
  // it terminates as `stopped`, which the state handler treats as a finish → reveal.
  core.on('input-request', function () {
    if (!running) { return; }
    inputAsked = true;
    core.stop();
  });
  core.on('state', function (state) {
    if (!running) { return; }
    if (state === 'finished' || state === 'stopped'
        || state === 'student-error' || state === 'runner-error') {
      running = false;
      showReveal(state);
      return;
    }
    // Graphics: the program is up and drawing, and (pgzero/pygame) will never finish on its
    // own. `running` reaching us IS the success signal — settle for a frame, then reveal
    // while the loop keeps going. An error state above still wins: it fires first and flips
    // `running`, which this timer re-checks.
    if (state === 'running' && isGraphics) {
      // Capture the go this timer belongs to. Without it, a graphics program that reaches
      // `running` and then terminates early (a student-error on an import or a missing asset)
      // reveals immediately and leaves this timer live; if the student re-runs
      // inside the settle window it fires against go 2, flips `running` mid-run and grades a
      // partially-accumulated stdout — a bogus attempt, counted and POSTed, that no automated
      // gate can see. Unreachable before the retry loop existed, reachable now.
      var settleToken = runToken;
      setTimeout(function () {
        if (settleToken !== runToken || !running || revealed) { return; }
        running = false;
        showReveal('running');
      }, GRAPHICS_SETTLE_MS);
    }
  });

  // ── reveal ───────────────────────────────────────────────────────────────────
  function setOutput(node, text) {
    if (text === '') {
      node.textContent = '(no output)';
      node.classList.add('is-empty');
    }
    else {
      node.textContent = text;
      node.classList.remove('is-empty');
    }
  }

  // Show or OMIT the actual-output block. Omitted — not rendered empty — whenever there is
  // nothing honest to put in it: on a miss (the answer is withheld), and on a reopened
  // predict whose record predates the stored output or is one of the two sub-types that
  // never stores one. Rendering it empty would print "(no output)", which claims the program
  // printed nothing.
  function setActualBlock(text) {
    var show = typeof text === 'string';
    actualLabel.hidden = !show;
    actualOut.hidden = !show;
    actualOut.classList.remove('is-answer');
    actualLabel.textContent = 'Actual output';
    actualLabel.classList.remove('is-answer');
    if (show) { setOutput(actualOut, text); }
  }

  // What the student should be SHOWN as the answer, once they have earned or asked for it.
  // In choice mode this is derived from the answer index on BOTH the live and the reopened
  // path — a graphics program's stdout is pygame-ce's boot banner, and even in console mode
  // naming the correct option is the honest answer to "which output does it produce?".
  function answerText() {
    if (isChoice) {
      return (cfg.answerIndex >= 0 && choices[cfg.answerIndex] != null)
        ? String(choices[cfg.answerIndex]) : '';
    }
    if (typeof sessionActual === 'string') { return sessionActual; }
    if (typeof resumedActual === 'string') { return resumedActual; }
    return null;
  }

  // The value that goes into the stored record. NOTHING for a choice-mode predict (its
  // reveal derives from the answer index) and NOTHING for a `mode:'text'` graphics predict
  // (its stdout is the SDL boot banner, and re-rendering that under "Actual output" is a
  // false claim about what the program printed). Truncated on the client — see
  // MAX_STORED_OUTPUT.
  function storableActual() {
    if (isChoice || isGraphics) { return undefined; }
    var value = (typeof sessionActual === 'string') ? sessionActual : resumedActual;
    if (typeof value !== 'string') { return undefined; }
    return value.length > MAX_STORED_OUTPUT ? value.slice(0, MAX_STORED_OUTPUT) : value;
  }

  // activity-action-buttons: render an option index as its label. Takes the index rather than
  // reading `selectedIdx` itself, because the reveal and the give-up must render the index
  // that was GRADED (the run-start snapshot, or the student's own last graded one) and not
  // whatever is selected now.
  function choiceText(idx) {
    return (idx >= 0 && choices[idx] != null) ? String(choices[idx]) : '';
  }

  function focusPrediction() {
    // activity-action-buttons: the button is no longer disabled on a miss, so this is no
    // longer rescuing focus from an element about to go away — it is simply the right landing
    // spot. A miss re-opens the activity, and the next thing the student does is change their
    // prediction, so focus belongs on the input rather than on the button they just pressed.
    // (It still runs BEFORE `submitResponse()` sets `saving`, so nothing is stealing focus
    // from a control that is about to be disabled.)
    try {
      if (textarea) { textarea.focus(); }
      else if (radios.length) { radios[0].input.focus(); }
    } catch (e) { /* focus is best-effort */ }
  }

  function showReveal(state) {
    if (revealed) { return; }
    revealed = true;

    // A re-run of a FINISHED predict reaches here too, and must change nothing: it stores no
    // second response and — the part no earlier rule covered — must not re-offer to close
    // the tab on a student who reopened the activity precisely in order to watch it again.
    if (finished) {
      updateRunEnabled();
      return;
    }

    updateRunEnabled();

    var actual = stdoutAcc;
    // On an error / a stdin park, note it alongside the output — correctness is still
    // computed against STDOUT. These notes are part of what the student is SHOWN, which is
    // why they are what gets stored rather than the raw accumulator.
    var notes = '';
    if (errored || state === 'student-error' || state === 'runner-error') {
      if (errorText) { notes += (actual && !/\n$/.test(actual) ? '\n' : '') + errorText; }
    }
    if (inputAsked) {
      notes += (actual || notes ? '\n' : '')
             + '(this program asked for keyboard input — predict activities work best with programs that do not)';
    }
    sessionActual = actual + (notes ? (actual ? '\n' : '') + notes : '');

    // activity-action-buttons: grade the RUN-START SNAPSHOT, never the live control. In choice
    // mode the `change` listener is guarded only by `if (finished)`, so a student could
    // otherwise switch to the correct option mid-run and be graded correct on a run made with
    // a different answer; in text mode the payload would disagree with the verdict.
    var correct, yourText;
    // predict-answer-feedback: the two live-run artifacts, TEXT MODE ONLY. Both stay null in
    // choice mode — `.predict-reflect` and the announcer below are written AFTER this fork
    // and serve BOTH modes, so without that a multiple-choice question would grow a "Check
    // your capital letters." hint. Neither is ever stored, POSTed or added to the submit
    // payload: they are live-run artifacts, which is why the respond route's Joi and the
    // predict persist literal are untouched by this change.
    var missHint = null;
    var passNote = null;
    // The side-by-side comparison shown in the finish prompt. Computed ONLY on a
    // leniency-assisted pass — i.e. only once the answer has been earned and is already on
    // screen. It must never be reachable from the miss path: highlighting the differing
    // characters of a withheld answer IS showing the answer.
    var passCompare = null;
    // The run states in which the inputs cannot support a claim, so no claim is made. A
    // graphics predict's stdout is the SDL boot banner; an errored or input-parked run's
    // `stdoutAcc` is partial and its traceback / parked-run prose lives only in the notes.
    var runState = {
      graphics: isGraphics,
      errored: errored || state === 'student-error' || state === 'runner-error',
      inputAsked: inputAsked
    };
    if (isChoice) {
      correct = runIdx === cfg.answerIndex;
      yourText = choiceText(runIdx);
    }
    else {
      yourText = runText;
      // Grade the student's prediction against what the program ACTUALLY printed
      // (the live-captured stdout) — never a teacher-typed value, which can be wrong.
      // The FULL prediction is graded; only storage is capped (see MAX_STORED_OUTPUT).
      var graded = gradePrediction(yourText, actual, normalizeCfg);
      correct = graded.correct;
      if (correct) {
        // Only when leniency actually made the difference — an exact match keeps today's
        // copy unchanged, and `graded.leniencyAssisted` is exactly "strict failed, the
        // activity's own settings passed".
        passNote = graded.leniencyAssisted
          ? explainForgivenPass(yourText, actual, normalizeCfg, runState) : null;
        // Tied to the NOTE, not to the pass: the same suppression states that make the note
        // dishonest make the comparison meaningless, and an exact match has nothing to show.
        if (passNote) { passCompare = comparePrediction(yourText, actual, normalizeCfg, runState); }
      } else {
        missHint = diagnosePredictMiss(yourText, actual, normalizeCfg, runState);
      }
    }

    attempts += 1;
    lastPrediction = isChoice ? runIdx : yourText;

    verdict.classList.remove('is-match', 'is-miss');
    setOutput(yourOut, yourText);

    if (correct) {
      finished = true;
      verdict.classList.add('is-match');
      verdict.textContent = '✓ Matched';
      reflect.textContent = 'Nice — your prediction matched the real output. Can you explain why?';
      // predict-answer-feedback: the forgiven-pass note, beneath the reflect sentence, which
      // is itself unchanged. A student must not be able to keep making the same mistake
      // without ever learning it differed from the real output.
      if (passNote && forgivenNote) {
        forgivenNote.textContent = passNote;
        forgivenNote.hidden = false;
      }
      // EARNED: the console comes back and the cover lifts.
      showAnswerSurfaces();
      setActualBlock(answerText());
      reveal.hidden = false;
      // ONE combined utterance: `announce()` writes '' then the message to a SINGLE element,
      // so a second call in the same tick would clobber the first and most screen readers
      // would drop one silently. The announced string is always today's announcement with
      // the pinned VISIBLE sentence spliced in — never separately authored copy.
      announce('Your prediction matched the real output.' + (passNote ? ' ' + passNote : ''));
      // activity-action-buttons: THIS CALL IS NEW AND IT IS LOAD-BEARING. `finished` was set
      // above, AFTER this function's last `updateRunEnabled()`, and this branch returns
      // without reaching another one — so without it the LIVE match path would never relabel
      // the button to "Run it again" or refresh its description. The give-up path and
      // `restore()` are each already covered by their own trailing calls, which is why only
      // this one was broken, and why no test catches it: the close-prompt fixture is a
      // RESUMED-finished predict.
      updateRunEnabled();
      updateGiveUp();
      submitResponse({ correct: true, gaveUp: false }).then(function (saved) {
        // THE AFFORDANCE FOLLOWS THE NOTE, NOT THE REASON FOR THE PASS. `offerReturn` exists
        // solely to give the student time to READ the note; where the note is suppressed
        // there is nothing to read, and its label ("Compare your answer with the real
        // output") would be pointing at a traceback or the input-parked prose.
        if (passNote) {
          // The label no longer says "compare with the real output" because the prompt now
          // SHOWS the comparison — telling a student to go and compare, while covering the
          // panel they would compare in, is the trap this replaced.
          offerAfterFinish(saved,
            'You passed, but your answer wasn’t exactly the same. The differences are highlighted.',
            true, passCompare);
          return;
        }
        offerAfterFinish(saved, 'Nice work — your prediction matched.');
      });
      return;
    }

    // ── a MISS: withhold, re-open, go again ─────────────────────────────────
    verdict.classList.add('is-miss');
    verdict.textContent = '✗ Not quite yet';
    // predict-answer-feedback: the diagnostic REPLACES the generic sentence rather than
    // joining it — "look at the code again" directly above "your logic is right, fix your
    // capitals" is contradictory advice. It is written into `.predict-reflect` itself, never
    // a sibling: this element is rewritten wholesale by the match branch, the miss branch,
    // the give-up handler and both `restore()` branches, so the hint inherits every existing
    // clear for free. A sibling would be written by nothing but this branch and would leave a
    // stale "Check your capital letters." inside the success and give-up panels.
    reflect.textContent = missHint
      || 'Not this time — look at the code again. What do you think it really does?';
    setActualBlock(null);           // the answer is withheld
    setCover(true);                 // graphics: the picture goes, the program keeps running
    reveal.hidden = false;

    // Re-open for another prediction: the input stays editable and the ONE button stays
    // enabled. Restoring a freeze here is exactly the lock-out this surface's cards report —
    // and leaving the button enabled for an UNCHANGED re-check is deliberate: it is what
    // keeps the give-up reachable for a student who will not change their answer.
    updateRunEnabled();
    updateGiveUp();

    // ONE utterance, with the pinned visible sentence spliced in at a fixed position — see
    // the match branch above for why a second `announce()` in the same tick is forbidden.
    announce('Not quite yet — your prediction did not match.'
      + (missHint ? ' ' + missHint : '') + ' You can predict again.');
    focusPrediction();

    submitResponse({ correct: false, gaveUp: false });
  }

  // ── the give-up ─────────────────────────────────────────────────────────────
  //
  // TWO SEQUENTIAL CONTROLS, never one. "Show me the answer" reveals and stores; the way
  // back appears afterwards. One button doing both would race the store it has to wait for.
  //
  // It stores the STUDENT'S OWN last prediction — never the revealed answer, which would
  // destroy their real answer and later show it back to them as though they had written it.
  giveUpBtn.addEventListener('click', function () {
    if (finished || giveUpBtn.disabled) { return; }
    finished = true;
    gaveUp = true;
    giveUpWrap.hidden = true;

    // REQUESTED: the console comes back and the cover lifts, exactly as on a match.
    showAnswerSurfaces();

    verdict.classList.remove('is-miss', 'is-match');
    verdict.classList.add('is-shown');
    verdict.textContent = 'You looked at the answer';
    setOutput(yourOut, isChoice
      ? choiceText(lastPrediction)
      : String(lastPrediction == null ? '' : lastPrediction));
    setActualBlock(answerText());
    // The give-up reveals THE answer, so the block carries a highlight of its own (predict.html).
    actualOut.classList.add('is-answer');
    actualLabel.textContent = 'Correct answer';
    actualLabel.classList.add('is-answer');
    reflect.textContent = 'Take a moment with this one. What does the code do that you did not expect?';
    reveal.hidden = false;
    // The way-back modal covers the page and blocks scrolling, so bring the answer on screen
    // BEFORE it opens — on a short window it otherwise lands below the fold, out of reach.
    try { actualOut.scrollIntoView({ block: 'center' }); } catch (e) { /* best-effort */ }

    updateRunEnabled();
    announce('The answer is now shown. Take your time reading it.');

    // The `submitted` latch is per RUN, and the last run has already spent it. A give-up is
    // its own event, not a second POST for that go, so it clears the latch explicitly —
    // without this the give-up stores NOTHING, the wall never clears and the student is left
    // exactly where the card found them, with a green "answer shown" panel in front of them.
    submitted = false;
    submitResponse({ correct: true, gaveUp: true }).then(function (saved) {
      // NO COUNTDOWN after a give-up: the student has just been handed the answer and needs
      // time to read it. A plain way back, and nothing that takes the page away.
      // one-tab-same-tab-launches: same-tab, and `stay` gives a "Stay here" dismiss first so the
      // student can close the panel and read the answer.
      if (saved === false) {
        offerReturn({ sameTab: true, warn: true, label: 'We couldn’t save that. Please tell your teacher.' });
      } else {
        offerReturn({ sameTab: true, stay: true, next: true, label: 'When you’ve read the answer, you can go back.' });
      }
    });
  });

  // ── store the attempt ────────────────────────────────────────────────────────
  //
  // EVERY POST carries the FULL key set. `recordOrReplace` `$set`s `answers` whole, so an
  // omitted key ERASES what is stored — the ordinary submit taken by every miss and every
  // pass is under exactly the same rules as the give-up.
  //
  // Resolves `true` on a stored write, `false` on a rejection, and `null` when there was
  // deliberately nothing to store (preview mode). The caller must NOT arm a countdown on
  // `false`: closing the tab over a failed save is this card's own symptom.
  function submitResponse(opts) {
    if (submitted) { return Promise.resolve(null); }
    submitted = true;
    // teacher-preview-split (task 9.2 / D7): predict is CLIENT-graded — this call only
    // STORES. In server-confirmed preview mode there is nothing to grade and nothing may be
    // written, so skip the store entirely. The reveal panel above has already shown the
    // teacher the same feedback a student gets.
    if (previewMode) { saveStatus.textContent = 'Preview — nothing is saved.'; return Promise.resolve(null); }

    // activity-action-buttons: the RUN-START SNAPSHOT, never the live control — the payload
    // and the verdict must agree, and a box cleared mid-run would otherwise send
    // `prediction: ''`, which fails both Joi alternatives (benign 200, handler skipped,
    // NOTHING stored) while the UI writes "Saved to your progress.".
    var prediction = isChoice ? runIdx : runText;
    if (opts.gaveUp) {
      // The student's OWN last submitted prediction, not whatever is in the box now.
      prediction = lastPrediction;
    }
    // TRUNCATE AFTER THE GIVE-UP OVERRIDE, AND ONLY ON A STRING. `lastPrediction` is raw (it
    // is `yourText` or `resumed.prediction`, neither ever capped) and may be `undefined`, so
    // capping the initial value alone would leave the give-up — the one path with no second
    // chance — storing nothing and never clearing the wall. An unguarded `.length` would throw
    // a TypeError from OUTSIDE the `try` below, which wraps only the fetch: it would escape the
    // give-up click handler, so `.then(offerReturn)` never runs and the student gets no store,
    // no way back and no error surface.
    if (typeof prediction === 'string' && prediction.length > MAX_STORED_OUTPUT) {
      prediction = prediction.slice(0, MAX_STORED_OUTPUT);
    }
    var answers = {
      prediction : prediction
      , correct    : opts.correct === true
      , attempts   : attempts
      , gaveUp     : opts.gaveUp === true
    };
    var actual = storableActual();
    if (typeof actual === 'string') { answers.actual = actual; }

    var url = '/api/courses/' + cfg.courseId + '/lessons/' + cfg.lessonId
            + '/materials/' + cfg.materialId + '/respond';
    var body = cfg.linkedCourseId
      ? { answers: answers, linkedCourseId: cfg.linkedCourseId }
      : { answers: answers };

    try {
      // activity-action-buttons: `saving` is set on the line IMMEDIATELY BEFORE the fetch and
      // NOWHERE ELSE. This function has FOUR exits and only this one makes a network call —
      // setting it at the top would latch it forever on the `previewMode` exit above, which
      // every teacher in preview-as-student takes, bricking the button with no error and no
      // recovery. It is cleared in the `.then`, the `.catch` AND the synchronous `catch (e)`,
      // and every set and clear is paired with `updateRunEnabled()` (the save-start /
      // save-settle re-evaluation points) — without those the `saving` conjunct is inert.
      saving = true;
      updateRunEnabled();
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        saving = false;
        updateRunEnabled();
        // A teacher preview (canManage) is 403'd by the roster course-student guard —
        // expected and silent; nothing is stored.
        if (res && res.ok && !canManage) {
          saveStatus.textContent = 'Saved to your progress.';
          return true;
        }
        return canManage ? null : false;
      }).catch(function () {
        saving = false;
        updateRunEnabled();
        return false;
      });
    } catch (e) {
      saving = false;
      updateRunEnabled();
      return Promise.resolve(false);
    }
  }

  // ── what happens after a finish ──────────────────────────────────────────────
  // predict-answer-feedback: `useReturn` swaps the close-on-success COUNTDOWN for the plain
  // way back, exactly as the give-up path already does and for the same stated reason — the
  // student has just been shown something they need a moment to read. The `saved === false`
  // (warn) and `saved === null` (preview / canManage) branches are UNCHANGED: they gate on
  // the outcome, not on the reason.
  function offerAfterFinish(saved, label, useReturn, compare) {
    // Never for a teacher. Predict carries BOTH flags and they are not interchangeable:
    // `preview` short-circuits the store before any POST, so there is no save to wait for,
    // and `canManage` is 403'd by the roster guard.
    if (previewMode || canManage) { return; }
    // one-tab-same-tab-launches: every ending is same-tab; the near-miss comparison return also
    // offers "Stay here" first (`stay`), so the comparison can be read.
    if (saved === false) {
      offerReturn({ sameTab: true, warn: true, label: 'We couldn’t save that. Please tell your teacher.' });
      return;
    }
    if (saved === null) { return; }
    if (useReturn) { offerReturn({ sameTab: true, stay: true, label: label, compare: compare || null }); return; }
    offerClose({ sameTab: true, label: label });
  }

  // ── resume ────────────────────────────────────────────────────────────────
  //
  // Reopening must not restart. These surfaces open in a tab students close, so a returning
  // student is the COMMONEST path, not an edge case — a resume that did nothing would make
  // the whole withholding design a no-op for most of the people it is for.
  function restore() {
    if (!resumed) { return; }

    // Pre-fill their previous prediction. In choice mode the stored value is the ORIGINAL
    // option index, which survives the per-load display shuffle.
    if (isChoice) {
      var idx = (typeof resumed.prediction === 'number') ? resumed.prediction : -1;
      radios.forEach(function (r) {
        if (r.index === idx) {
          r.input.checked = true;
          r.label.classList.add('is-selected');
          selectedIdx = idx;
        }
      });
    }
    else if (textarea && resumed.prediction != null) {
      textarea.value = String(resumed.prediction);
    }

    var wasFinished = resumed.correct === true || resumed.gaveUp === true;

    if (wasFinished) {
      finished = true;
      gaveUp = resumed.gaveUp === true;
      verdict.classList.remove('is-match', 'is-miss', 'is-shown');
      if (gaveUp) {
        verdict.classList.add('is-shown');
        verdict.textContent = 'You looked at the answer';
        reflect.textContent = 'You asked to see this one. What does the code do that you did not expect?';
      } else {
        verdict.classList.add('is-match');
        verdict.textContent = '✓ Matched';
        reflect.textContent = 'You matched the real output on this one.';
      }
      setOutput(yourOut, isChoice
        ? choiceText(selectedIdx)
        : (textarea ? textarea.value : ''));
      // OMITTED when nothing was stored — never rendered empty.
      setActualBlock(answerText());
      reveal.hidden = false;
      // The answer was earned or requested last time, so it stays visible now.
      showAnswerSurfaces();
      saveStatus.textContent = 'Saved to your progress.';
      // NOTHING is armed on page load — the way back is the ordinary browser tab, and a
      // countdown here would close the tab on a student who came back deliberately.
      return;
    }

    // MID-RETRY — the commonest path of all. The input stays editable and the button is
    // enabled as soon as the runner is ready: freezing anything here would reproduce the exact
    // lock-out these cards report. The hint chain resolves to the "attempt n" form, which is
    // the only one that agrees with the "✗ Not quite yet" verdict painted just below.
    verdict.classList.add('is-miss');
    verdict.textContent = '✗ Not quite yet';
    reflect.textContent = 'You did not match last time. Look at the code again and predict again.';
    setActualBlock(null);
    reveal.hidden = false;
    // Armed at LOAD for a graphics predict, exactly as the console is hidden at load: without
    // this the picture is on screen before the student has predicted anything.
    setCover(true);
  }

  updatePromptVisibility();
  restore();
  // The construction-time evaluation point: this fills the button's label AND its description
  // for the first time (nothing sets either at creation, so the chain is their only writer).
  updateRunEnabled();
  updateGiveUp();
}
